import { getRaceClassProfile } from "./balance";
import type { KartState, RaceState } from "./types";

export const DRAFT_MIN_GAP = 5;
export const DRAFT_MAX_GAP = 20;
export const DRAFT_MAX_LATERAL = 3.1;
export const DRAFT_CHARGE_SECONDS = 0.85;
export const DRAFT_BOOST_SECONDS = 0.85;
export const DRAFT_COOLDOWN_SECONDS = 2.8;

function canDraft(kart: KartState): boolean {
  return kart.finishedAt === null
    && kart.surface !== "offroad"
    && !kart.airborne
    && kart.spinTime <= 0
    && kart.recoveryTime <= 0
    && kart.speed >= 12;
}

function nearestDraftTarget(kart: KartState, race: RaceState): KartState | null {
  if (!canDraft(kart)) return null;
  const forwardX = Math.sin(kart.yaw);
  const forwardZ = Math.cos(kart.yaw);
  return race.karts
    .filter((candidate) => candidate.id !== kart.id && canDraft(candidate) && candidate.surface === kart.surface)
    .map((candidate) => ({
      candidate,
      gap: candidate.distance - kart.distance,
      lateralGap: Math.abs(candidate.lateral - kart.lateral),
      headingDot: forwardX * Math.sin(candidate.yaw) + forwardZ * Math.cos(candidate.yaw),
      heightGap: Math.abs(candidate.y - kart.y),
    }))
    .filter(({ gap, lateralGap, headingDot, heightGap }) => gap >= DRAFT_MIN_GAP && gap <= DRAFT_MAX_GAP && lateralGap <= DRAFT_MAX_LATERAL && headingDot >= 0.9 && heightGap <= 1.8)
    .sort((a, b) => a.gap - b.gap || a.candidate.id.localeCompare(b.candidate.id))[0]?.candidate ?? null;
}

export function stepDrafting(race: RaceState, dt: number): void {
  if (race.phase !== "race") {
    for (const kart of race.karts) {
      kart.draftTargetId = null;
      kart.draftCharge = 0;
    }
    return;
  }

  const tuning = getRaceClassProfile(race.raceClass);
  for (const kart of race.karts) {
    if (kart.draftBoostTime > 0 || kart.draftCooldown > 0 || !canDraft(kart)) {
      kart.draftTargetId = null;
      kart.draftCharge = Math.max(0, kart.draftCharge - dt * 2.5);
      continue;
    }
    const target = nearestDraftTarget(kart, race);
    if (!target) {
      kart.draftTargetId = null;
      kart.draftCharge = Math.max(0, kart.draftCharge - dt * 1.8);
      continue;
    }
    if (kart.draftTargetId !== target.id) {
      kart.draftTargetId = target.id;
      kart.draftCharge = 0;
    }
    kart.draftCharge = Math.min(1, kart.draftCharge + dt / DRAFT_CHARGE_SECONDS);
    if (kart.draftCharge < 1) continue;

    const forwardX = Math.sin(kart.yaw);
    const forwardZ = Math.cos(kart.yaw);
    const longitudinal = kart.velocityX * forwardX + kart.velocityZ * forwardZ;
    const releaseSpeed = Math.max(longitudinal + tuning.maxSpeed * 0.075, tuning.maxSpeed * 0.82);
    kart.velocityX += forwardX * Math.max(0, releaseSpeed - longitudinal);
    kart.velocityZ += forwardZ * Math.max(0, releaseSpeed - longitudinal);
    kart.speed = Math.hypot(kart.velocityX, kart.velocityZ);
    kart.draftBoostTime = DRAFT_BOOST_SECONDS;
    kart.draftCooldown = DRAFT_COOLDOWN_SECONDS;
    kart.draftBoostCount += 1;
    kart.draftCharge = 0;
    kart.draftTargetId = null;
  }
}
